import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~ai-tutor/components/ui/card';
import type { KnowledgeProfile } from '~ai-tutor/lib/chatflow/shared/knowledgeProfile/schema';

type KnowledgeProfileDisplayProps = {
  knowledgeProfile: KnowledgeProfile;
};

export const KnowledgeProfileDisplay = ({ knowledgeProfile }: KnowledgeProfileDisplayProps) => {
  const { t } = useTranslation();

  const { coveredTopics, knowledgeGaps, overallSummary, strengths } = knowledgeProfile;

  return (
    <div className="space-y-6 pt-8">
      <Card>
        <CardHeader>
          <CardTitle>{t('knowledgeProfile.overview.title')}</CardTitle>
          <CardDescription>{t('knowledgeProfile.overview.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {overallSummary && <p className="leading-relaxed">{overallSummary}</p>}
          <div className="mt-24 flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center sm:gap-16">
            <div className="flex items-center gap-4">
              <CheckCircle2 className="text-feedback-success" />
              <span className="text-lg font-semibold">{strengths.length}</span>
              <span className="text-muted-foreground text-sm">
                {t('knowledgeProfile.overview.strengths')}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <CheckCircle2 className="text-brand-subtle" />
              <span className="text-lg font-semibold">{coveredTopics.length}</span>
              <span className="text-muted-foreground text-sm">
                {t('knowledgeProfile.overview.coveredTopics')}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <XCircle className="text-feedback-error h-5 w-5" />
              <span className="text-lg font-semibold">{knowledgeGaps.length}</span>
              <span className="text-muted-foreground text-sm">
                {t('knowledgeProfile.overview.knowledgeGaps')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {strengths.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('knowledgeProfile.strengths.title')}</CardTitle>
            <CardDescription>{t('knowledgeProfile.strengths.description')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="grid gap-3 sm:grid-cols-2">
              {strengths.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="text-feedback-success mt-0.5 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {coveredTopics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('knowledgeProfile.covered.title')}</CardTitle>
            <CardDescription>{t('knowledgeProfile.covered.description')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="grid gap-3 sm:grid-cols-2">
              {coveredTopics.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand-subtle mt-0.5 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {knowledgeGaps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('knowledgeProfile.knowledgeGaps.title')}</CardTitle>
            <CardDescription>{t('knowledgeProfile.knowledgeGaps.description')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="grid gap-3 sm:grid-cols-2">
              {knowledgeGaps.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <XCircle className="text-feedback-error mt-0.5 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
